/**
 * Meeting Heartbeat Service
 *
 * Provides heartbeat functionality for meeting participants to prevent
 * orphaned sessions when clients disconnect unexpectedly (app crash, network loss).
 *
 * The Cloud Function `cleanupMeetingParticipants` runs every 5 minutes and marks
 * participants as inactive if they haven't sent a heartbeat in 5+ minutes.
 *
 * Supports two patterns:
 * 1. Participants array in meeting document (web app)
 * 2. Peers subcollection (iOS app)
 */

import { doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';

// Heartbeat interval in milliseconds (15 seconds - matches iOS)
// Reduced from 45s to detect stale participants faster (max ghost time ~75s)
const HEARTBEAT_INTERVAL_MS = 15 * 1000;

// Track active heartbeat intervals
const activeHeartbeats = new Map();

/**
 * Start sending heartbeats for a meeting participant
 *
 * @param {string} meetingId - The meeting ID
 * @param {string} participantId - The participant's ID in the meeting
 * @returns {Function} Cleanup function to stop the heartbeat
 */
export function startHeartbeat(meetingId, participantId) {
  if (!meetingId || !participantId) {
    console.error('startHeartbeat: meetingId and participantId are required');
    return () => {};
  }

  const heartbeatKey = `${meetingId}:${participantId}`;

  // Stop any existing heartbeat for this meeting/participant combo
  stopHeartbeat(meetingId, participantId);

  console.log(`[Heartbeat] Starting heartbeat for meeting ${meetingId}, participant ${participantId}`);

  // Send initial heartbeat
  sendHeartbeat(meetingId, participantId);

  // Set up interval
  const intervalId = setInterval(() => {
    sendHeartbeat(meetingId, participantId);
  }, HEARTBEAT_INTERVAL_MS);

  activeHeartbeats.set(heartbeatKey, intervalId);

  // Return cleanup function
  return () => stopHeartbeat(meetingId, participantId);
}

/**
 * Stop sending heartbeats for a meeting participant
 *
 * @param {string} meetingId - The meeting ID
 * @param {string} peerId - The participant's peer ID
 */
export function stopHeartbeat(meetingId, peerId) {
  const heartbeatKey = `${meetingId}:${peerId}`;
  const intervalId = activeHeartbeats.get(heartbeatKey);

  if (intervalId) {
    console.log(`[Heartbeat] Stopping heartbeat for meeting ${meetingId}, peer ${peerId}`);
    clearInterval(intervalId);
    activeHeartbeats.delete(heartbeatKey);
  }
}

/**
 * Stop all active heartbeats (call on app unmount/logout)
 */
export function stopAllHeartbeats() {
  console.log(`[Heartbeat] Stopping all heartbeats (${activeHeartbeats.size} active)`);
  activeHeartbeats.forEach((intervalId) => {
    clearInterval(intervalId);
  });
  activeHeartbeats.clear();
}

/**
 * Send a single heartbeat update
 * Updates the participant's lastHeartbeat in the meeting's participants array
 *
 * @param {string} meetingId - The meeting ID
 * @param {string} participantId - The participant's ID
 */
async function sendHeartbeat(meetingId, participantId) {
  try {
    const meetingRef = doc(db, 'meetings', meetingId);
    const meetingSnap = await getDoc(meetingRef);

    if (!meetingSnap.exists()) {
      console.warn(`[Heartbeat] Meeting ${meetingId} not found`);
      return;
    }

    const meetingData = meetingSnap.data();
    const participants = meetingData.participants || [];

    // Find and update the participant
    const updatedParticipants = participants.map(p => {
      if (p.id === participantId) {
        return {
          ...p,
          lastHeartbeat: Date.now(),
          isActive: true
        };
      }
      return p;
    });

    await updateDoc(meetingRef, {
      participants: updatedParticipants
    });

    console.log(`[Heartbeat] Sent heartbeat for meeting ${meetingId}, participant ${participantId}`);
  } catch (error) {
    // If direct update fails, try Cloud Function (handles auth verification)
    try {
      const functions = getFunctions();
      const updateHeartbeatFn = httpsCallable(functions, 'updateHeartbeat');
      await updateHeartbeatFn({ meetingId, peerId: participantId });
      console.log(`[Heartbeat] Sent heartbeat via Cloud Function for meeting ${meetingId}`);
    } catch (cfError) {
      console.warn(`[Heartbeat] Failed to send heartbeat:`, cfError.message);
      // Don't throw - heartbeat failures shouldn't crash the app
    }
  }
}

/**
 * Hook: Use heartbeat in a React component
 *
 * @example
 * // In VideoCall component:
 * useEffect(() => {
 *   if (meetingId && peerId) {
 *     const cleanup = startHeartbeat(meetingId, peerId);
 *     return cleanup;
 *   }
 * }, [meetingId, peerId]);
 */

// Export for testing
export { sendHeartbeat, HEARTBEAT_INTERVAL_MS };
